/**
 * Header custom elements: custom-header + sticky-header.
 * Geometry is cached and refreshed on resize / announcement measure — not on scroll.
 */

function throttle(fn, delay = 0) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn.apply(this, args);
  };
}

class CustomHeader extends HTMLElement {
  constructor() {
    super();
    this.setMenusBounds = this.setMenusBounds.bind(this);
    this.setHeight = this.setHeight.bind(this);
    this._onAnnouncementHeightChange = this.onAnnouncementMeasure.bind(this);
    this._announcementBarEl = null;
    this._announcementBarResolved = false;
    this._headerSectionEl = null;
    this._headerSectionResolved = false;
    this._announcementNaturalHeight = 0;
    this._currentAnnouncementHeight = null;
    this._currentAnnouncementHeightDynamic = null;
    this._headerHeight = 0;
    this._stickyHeaderHeight = null;
    this._heightRaf = 0;
    this._menuBoundsRaf = 0;
    this._headerHeightPublished = false;
  }

  get allowTransparent() {
    return this.getAttribute('data-header-transparent') === 'true';
  }

  get isNotSticky() {
    return this.getAttribute('data-sticky-type') === 'none';
  }

  get headerSection() {
    if (!this._headerSectionResolved) {
      this._headerSectionEl = document.querySelector('.header-main');
      this._headerSectionResolved = true;
    }
    return this._headerSectionEl;
  }

  getAnnouncementBar() {
    if (!this._announcementBarResolved) {
      this._announcementBarEl = document.querySelector('[is="announcement-bar"]');
      this._announcementBarResolved = true;
    }

    return this._announcementBarEl;
  }

  connectedCallback() {
    this.init();

    document.addEventListener('announcement:height', this._onAnnouncementHeightChange);

    if (window.ResizeObserver) {
      // Use RO box size — avoid getBoundingClientRect on every resize tick
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const fromBox = entry.borderBoxSize?.[0]?.blockSize;
        const height =
          typeof fromBox === 'number' && fromBox > 0
            ? fromBox
            : entry.contentRect?.height || 0;
        if (height > 0) {
          this.applyHeaderHeight(height);
        }
        this.scheduleMenuBounds();
      });

      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();

    document.removeEventListener('announcement:height', this._onAnnouncementHeightChange);

    if (this._onHideTransitionEnd) {
      this.headerSection?.removeEventListener('transitionend', this._onHideTransitionEnd);
    }

    if (this._heightRaf) {
      cancelAnimationFrame(this._heightRaf);
      this._heightRaf = 0;
    }
    if (this._menuBoundsRaf) {
      cancelAnimationFrame(this._menuBoundsRaf);
      this._menuBoundsRaf = 0;
    }

    this._announcementBarEl = null;
    this._announcementBarResolved = false;
    this._headerSectionEl = null;
    this._headerSectionResolved = false;
    clearTimeout(this._transparentTimer);
    clearTimeout(this._hideHeaderTimer);
  }

  init() {
    this.setHeight();
    // Pull cache only — never offsetHeight here (announcement-bar measures + fires event)
    this.hydrateAnnouncementFromCache();
    // this.updateTransparentState();
  }

  /**
   * ----------------------------------------
   * ANNOUNCEMENT BAR
   * ----------------------------------------
   *
   * announcement-bar.js owns measuring (offsetHeight)
   * and --announcementHeight. Header only caches the
   * value from announcement:height / announcementBarHeight
   * and owns --dynamicAnnouncementHeight on scroll.
   */

  /**
   * Read announcement's cached height only — no layout query.
   */
  hydrateAnnouncementFromCache() {
    const announcement = this.getAnnouncementBar();
    if (!announcement) {
      this._announcementNaturalHeight = 0;
      this._currentAnnouncementHeight = 0;
      this.setAnnouncementHeightDynamic(0);
      return;
    }

    const cached = Number(announcement.announcementBarHeight) || 0;
    if (cached > 0) {
      this._announcementNaturalHeight = cached;
      this._currentAnnouncementHeight = cached;
      this.setAnnouncementHeightDynamic(this.isAnnouncementAway() ? 0 : cached);
    }
  }

  onAnnouncementMeasure(event) {
    const measured = event?.detail?.height;
    if (typeof measured === 'number' && measured >= 0) {
      this._announcementNaturalHeight = measured;
      // Bar already wrote --announcementHeight — don't rewrite (avoids style thrash)
      this._currentAnnouncementHeight = measured;
      this.setAnnouncementHeightDynamic(this.isAnnouncementAway() ? 0 : measured);
      return;
    }

    this.hydrateAnnouncementFromCache();
  }

  setAnnouncementHeightDynamic(height) {
    // Non-sticky header does not drive sticky offsets.
    if (this.isNotSticky) {
      return;
    }

    const next = Number(height || 0);
    if (this._currentAnnouncementHeightDynamic === next) {
      return;
    }
    this._currentAnnouncementHeightDynamic = next;
    document.documentElement.style.setProperty(
      '--dynamicAnnouncementHeight',
      `${next.toFixed(1)}px`,
    );
  }

  /** Announcement has scrolled out of the top — sticky filters should not offset for it. */
  isAnnouncementAway() {
    return Boolean(this.isScrolled);
  }

  getAnnouncementHeight() {
    return this._announcementNaturalHeight || 0;
  }

  /** Cached header height — refreshed in setHeight / resize, never measured on scroll. */
  getCachedHeaderHeight() {
    return this._headerHeight || this.headerBounds?.height || 0;
  }

  /** Sync from announcement cache only (resize) — no offsetHeight. */
  updateAnnouncementHeight() {
    this.hydrateAnnouncementFromCache();
  }

  /**
   * ----------------------------------------
   * TRANSPARENT HEADER
   * ----------------------------------------
   */

  updateTransparentState() {
    if (!this.headerSection) {
      return;
    }

    if (this.allowTransparent) {
      this.classList.add(
        'header-transparent',
        'desktop-header-transparent',
        'mobile-header-transparent',
      );

      this.classList.add('no-animate');
      clearTimeout(this._transparentTimer);
      this._transparentTimer = setTimeout(() => {
        this.classList.remove('no-animate');
      }, 500);
    }
  }

  /**
   * ----------------------------------------
   * HEADER HEIGHT
   * ----------------------------------------
   */

  applyHeaderHeight(height) {
    if (!(height > 0)) return;
    if (this._headerHeight === height && this._headerHeightPublished) return;
    this._headerHeight = height;
    this._headerHeightPublished = true;
    document.documentElement.style.setProperty('--headerHeight', `${height.toFixed(1)}px`);
  }

  setHeight() {
    if (this._heightRaf) cancelAnimationFrame(this._heightRaf);

    this._heightRaf = requestAnimationFrame(() => {
      this._heightRaf = 0;
      // One read for initial paint; RO handles later size changes without getBoundingClientRect
      const height = this.getBoundingClientRect().height;
      this.applyHeaderHeight(height);
      this.scheduleMenuBounds();
    });
  }

  scheduleMenuBounds() {
    if (this._menuBoundsRaf) return;
    // Next frame after height write — menus not needed for LCP / first paint
    this._menuBoundsRaf = requestAnimationFrame(() => {
      this._menuBoundsRaf = 0;
      this.setMenusBounds();
    });
  }

  /**
   * ----------------------------------------
   * MENU BOUNDS
   * ----------------------------------------
   * Split collect (reads) / apply (writes) so callers can batch with other measures.
   */

  collectMenuBounds() {
    const menus = this.querySelectorAll(
      'details[is="details-dropdown"][level="top"], details[is="details-mega"]',
    );
    if (!menus.length) return [];

    const wrapper = this.querySelector('.header-wrapper');
    const measured = [];

    menus.forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      const isMega = details.matches('details[is="details-mega"]');
      const panel = details.querySelector(isMega ? ':scope > .mega-menu' : ':scope > .dropdown');
      if (!panel) return;

      const summaryRect = summary.getBoundingClientRect();
      const reference = isMega ? panel.offsetParent || wrapper || this : panel;
      const referenceRect = reference.getBoundingClientRect();

      measured.push({
        details,
        left: summaryRect.left - referenceRect.left,
        top: summaryRect.top - referenceRect.top,
        width: summaryRect.width,
      });
    });

    return measured;
  }

  applyMenuBounds(measured) {
    if (!measured?.length) return;
    measured.forEach(({ details, left, top, width }) => {
      details.style.setProperty('--extraLeft', `${left.toFixed(1)}px`);
      details.style.setProperty('--extraTop', `${top.toFixed(1)}px`);
      details.style.setProperty('--extraWidth', `${width.toFixed(1)}px`);
    });
  }

  setMenusBounds() {
    this.applyMenuBounds(this.collectMenuBounds());
  }
}

if (!customElements.get('custom-header')) {
  customElements.define('custom-header', CustomHeader, { extends: 'header' });
}

class StickyHeader extends CustomHeader {
  constructor() {
    super();
    this.isScrolled = false;
    this.isHidden = false;
    this.currentScrollTop = window.scrollY;
    this.buffer = 10;
    // Extra px below mid first-section before header-hidden (on-scroll-up only).
    this.hideBuffer = 200;
    this._hideScrollY = this.hideBuffer;
    this._userScrollActive = false;
    this._userScrollIdleTimer = null;
    this._onScroll = throttle(this.onScrollHandler.bind(this));
    this._onResize = throttle(this.onResizeHandler.bind(this), 100);
    this._markUserScroll = this._markUserScroll.bind(this);
  }

  get stickyType() {
    return this.getAttribute('data-sticky-type') || 'always';
  }

  get isAlwaysSticky() {
    return this.stickyType === 'always';
  }

  get isScrollUp() {
    return this.stickyType === 'on-scroll-up';
  }

  get isNotSticky() {
    return this.stickyType === 'none';
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.headerSection) {
      return;
    }

    this.currentScrollTop = window.scrollY;
    this.updateHeaderBounds();
    this.updateHideScrollY();
    this.beforeInit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._userScrollIdleTimer);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('wheel', this._markUserScroll);
    window.removeEventListener('touchmove', this._markUserScroll);
  }

  /**
   * ----------------------------------------
   * HEADER BOUNDS
   * ----------------------------------------
   */

  updateHeaderBounds() {
    if (!this.headerSection) {
      return;
    }
    // Prefer cached height — avoid getBoundingClientRect when setHeight/RO already measured
    if (this._headerHeight > 0) {
      this.headerBounds = { height: this._headerHeight };
      return;
    }
    this.headerBounds = this.headerSection.getBoundingClientRect();
    if (this.headerBounds.height > 0) {
      this._headerHeight = this.headerBounds.height;
    }
  }

  /**
   * Hide only after mid first-section + ~200px (cached — not on scroll).
   */
  updateHideScrollY() {
    const firstSection = document.querySelector('#MainContent > .shopify-section');
    if (!firstSection) {
      this._hideScrollY = this.hideBuffer;
      return;
    }
    this._hideScrollY = Math.round(firstSection.offsetHeight * 0.2) + this.hideBuffer;
  }

  /**
   * ----------------------------------------
   * INIT
   * ----------------------------------------
   */

  beforeInit() {
    if (this.isNotSticky) {
      this.headerSection.classList.remove(
        'header-sticky',
        'header-scrolled',
        'header-nav-scrolled',
        'header-hidden',
      );
      return;
    }

    this.headerSection.classList.add('header-sticky');
    this.headerSection.setAttribute('data-sticky-type', this.stickyType);
    window.addEventListener('scroll', this._onScroll, {
      passive: true,
    });

    window.addEventListener('resize', this._onResize, {
      passive: true,
    });

    // Real user input only — Lenis residual easing still fires scroll events.
    window.addEventListener('wheel', this._markUserScroll, { passive: true });
    window.addEventListener('touchmove', this._markUserScroll, {
      passive: true,
    });

    requestAnimationFrame(() => {
      this.updateHeaderBounds();
      this.onScrollHandler();
    });
  }

  _markUserScroll() {
    this._userScrollActive = true;
    clearTimeout(this._userScrollIdleTimer);
    this._userScrollIdleTimer = setTimeout(() => {
      this._userScrollActive = false;
    }, 180);
  }

  _shouldCloseMenusOnScroll(scrollTop, navThreshold) {
    if (!this._userScrollActive) return false;
    if (!(this.isScrolled && scrollTop > navThreshold)) return false;
    if (this.headerSection?.matches(':hover')) return false;
    return true;
  }

  /**
   * ----------------------------------------
   * RESIZE
   * ----------------------------------------
   */

  onResizeHandler() {
    this.updateHeaderBounds();
    this.updateHideScrollY();
    this.setHeight();
    // Announcement ResizeObserver / debounce owns remeasure; only pull cache here
    this.hydrateAnnouncementFromCache();
    this.updateStickyHeaderHeight();
  }

  /**
   * ----------------------------------------
   * STICKY HEADER HEIGHT
   * ----------------------------------------
   */

  setStickyHeaderHeight(height) {
    const value = typeof height === 'number' ? `${height.toFixed(1)}px` : height;
    if (this._stickyHeaderHeight === value) return;
    this._stickyHeaderHeight = value;
    document.documentElement.style.setProperty('--stickyheaderHeight', value);
  }

  updateStickyHeaderHeight() {
    if (!this.headerSection) {
      return;
    }

    if (this.isHidden) {
      this.setStickyHeaderHeight(0);
      return;
    }

    // Use cache only — no getBoundingClientRect on scroll path
    this.setStickyHeaderHeight(this.getCachedHeaderHeight());
  }

  /**
   * ----------------------------------------
   * HIDE HEADER
   * ----------------------------------------
   *
   * Slide header up first while keeping
   * --stickyheaderHeight unchanged, then
   * set it to 0 after the transition.
   */

  hideHeader() {
    if (this.isHidden) {
      return;
    }

    // Keep current height during slide-up animation (cached — no layout read)
    this.setStickyHeaderHeight(this.getCachedHeaderHeight());

    this.isHidden = true;

    this.classList.add('header-hidden');
    clearTimeout(this._hideHeaderTimer);
    const finishHide = () => {
      if (this.isHidden) {
        this.setStickyHeaderHeight(0);
      }
    };

    this._onHideTransitionEnd = (event) => {
      if (event.target !== this.headerSection || event.propertyName !== 'transform') {
        return;
      }

      this.headerSection.removeEventListener('transitionend', this._onHideTransitionEnd);
      finishHide();
    };

    this.headerSection.addEventListener('transitionend', this._onHideTransitionEnd);
    this._hideHeaderTimer = setTimeout(finishHide, 420);
  }

  /**
   * ----------------------------------------
   * SHOW HEADER
   * ----------------------------------------
   */

  showHeader() {
    if (!this.isHidden) {
      return;
    }

    clearTimeout(this._hideHeaderTimer);
    if (this._onHideTransitionEnd) {
      this.headerSection.removeEventListener('transitionend', this._onHideTransitionEnd);
    }
    this.isHidden = false;
    // Restore height before slide-in so layout does not jump (cached)
    this.setStickyHeaderHeight(this.getCachedHeaderHeight());

    requestAnimationFrame(() => {
      this.classList.remove('header-hidden');
    });
  }

  /**
   * ----------------------------------------
   * SCROLLED STATE
   * ----------------------------------------
   */

  setScrolledState(isScrolled) {
    if (isScrolled === this.isScrolled) {
      return;
    }

    this.isScrolled = isScrolled;

    // Announcement is past the fold once header enters scrolled state.
    this.setAnnouncementHeightDynamic(isScrolled ? 0 : this.getAnnouncementHeight());

    if (isScrolled) {
      this.headerSection.classList.add('header-scrolled');
      this.headerSection.classList.remove('header-nav-scrolled');
      this.updateStickyHeaderHeight();
      document.dispatchEvent(
        new CustomEvent('header:scrolled', {
          bubbles: true,
          detail: {
            scrolled: true,
          },
        }),
      );
    } else {
      this.headerSection.classList.remove('header-scrolled');
      this.headerSection.classList.remove('header-nav-scrolled');
      this.showHeader();
      this.updateStickyHeaderHeight();
      document.dispatchEvent(
        new CustomEvent('header:scrolled', {
          bubbles: true,
          detail: {
            scrolled: false,
          },
        }),
      );
    }
  }

  /**
   * ----------------------------------------
   * SCROLL — no geometry reads; only cached values + class toggles
   * ----------------------------------------
   */

  onScrollHandler() {
    if (!this.headerSection || this.isNotSticky) {
      return;
    }
    const scrollTop = window.scrollY;
    const announcementHeight = this.getAnnouncementHeight();
    const headerHeight = this.getCachedHeaderHeight();
    const scrollDownThreshold = announcementHeight + this.buffer;
    const scrollUpThreshold = Math.max(announcementHeight - this.buffer, 0);

    const isScrolled = this.isScrolled
      ? scrollTop > scrollUpThreshold
      : scrollTop > scrollDownThreshold;

    if (!this.isScrolled && isScrolled) {
      this.setScrolledState(true);
    } else if (this.isScrolled && !isScrolled) {
      this.setScrolledState(false);
    }

    /**
     * --------------------------------------
     * NAV SCROLLED
     * --------------------------------------
     */

    const navThreshold = announcementHeight + headerHeight;
    if (this.isScrolled && scrollTop > navThreshold) {
      this.headerSection.classList.add('header-nav-scrolled');
      // Close menus only during active wheel/touch — not Lenis residual easing.
      if (this._shouldCloseMenusOnScroll(scrollTop, navThreshold)) {
        this.headerSection.querySelectorAll('details').forEach((details) => {
          if (details.hasAttribute('open')) {
            details.open = false;
          }
        });
      }
    } else {
      this.headerSection.classList.remove('header-nav-scrolled');
    }

    /**
     * --------------------------------------
     * ON SCROLL UP
     * --------------------------------------
     *
     * header-scrolled: announcement threshold (above).
     * header-hidden: mid first-section + ~200px (cached).
     * Scroll up: remove hidden immediately (same as before).
     * always: never hide.
     */

    if (this.isScrollUp && this.isScrolled) {
      const hideAt = Math.max(scrollDownThreshold + this.hideBuffer, this._hideScrollY);
      if (scrollTop > this.currentScrollTop) {
        // Scrolling DOWN — wait until mid first-section (+200px) before hide
        if (scrollTop > hideAt) {
          this.hideHeader();
        }
      } else if (scrollTop < this.currentScrollTop) {
        // Scrolling UP — reveal immediately
        this.showHeader();
      }
    }
    // Always sticky: no hide/show work on scroll (was calling showHeader every frame)

    this.currentScrollTop = scrollTop;
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader, { extends: 'header' });
}
